/**
 * The behaviour content may select. Each name is implemented by engine code; content chooses among
 * them and supplies parameters, and validation refuses anything else before publication. Adding a
 * name here means implementing it first. `content/engine_handlers.test.ts` holds these lists equal
 * to the engine's own types.
 */
export const ENGINE_HANDLERS = {
    /** USE_ITEM activations (server campaigns/atoms/effect.ts handleActivateEffectMeta). */
    itemActivationEffects: ["light"],
    /** Projectile behaviours (mechanics/item_mechanics.ts Projectile.resolveAmmoMetaFromAmmo). */
    ammoTypes: ["arrow", "webAmmo"],
    /** Attack animations a weapon or race plays (client lpc_composite.ts). */
    attackActions: ["thrust", "slash", "shoot"],
    /** NPC kinds a content NPC spec creates: a sprite marker or an LPC creature (npc/forge/npc_forge.ts). */
    npcTypes: ["marker", "zombie"],
    /** Entity flags the engine reads on encounter creatures. */
    encounterFlags: ["devKillable", "dropsLootOnDeath", "isChicken", "isRabbit", "isWildEnemy", "noContactPush", "nonAggro", "patrolWander"],
    /** Item and item-class characteristics (item/item.ts ItemCharacteristics), by value type. */
    characteristics: {
        boolean: ["equippable", "readyable", "buildable", "deactivateAfterPlacement", "exclusiveReadiness", "stackable", "useable", "primaryUseable", "campfireFuel", "useOnBuild", "useHasWorldEffect", "stableAngleOnDrop", "isCurrency", "isAmmo", "unsellable", "unbuyable", "hasDurability"],
        buildType: ["wall", "decor", "entity", "roof", "floor", "floor2"],
        placementAction: ["use", "build"],
    },
    /**
     * Content the engine names in code: hunger and healing foods, currency, chests, building
     * pieces, outfits, wildlife. Removing or renaming one needs an engine change first, so
     * validation refuses content without them. `engine_handlers.test.ts` keeps this list complete.
     */
    required: {
        items: [
            "armor-leather", "armor-plate", "arrow", "axe", "bauldron", "bow", "campfire", "cape-solid",
            "castlekey", "chest-wood", "coal", "copper", "crossbow", "dagger", "door-wood", "drumstick",
            "ember-charge", "flail", "floor-opening", "floor-wood", "flower-single-blue", "flower-single-purple",
            "flower-single-red", "flower-single-yellow", "gold", "halberd", "hatch-wood", "hide", "iron", "iron-pickaxe", "legion",
            "longsleeve", "longsword", "mace", "metal-bar-copper", "metal-bar-gold", "metal-bar-iron",
            "metal-bar-silver", "mushroom-purple", "mushroom-red", "mushroom-yellow", "none", "pants",
            "pants-armor", "planks", "rapier", "roof-wood", "rope", "saber", "sandals", "scythe", "seed",
            "shard-metal", "shield-crusader", "shield-round", "shield-spartan", "shoes", "shoes-armor", "silver",
            "skirts", "slippers", "snare-rabbit", "spear", "staff", "stairs", "stick", "stool1", "straw",
            "table-large-rough", "table-mid-round", "torch", "wall-stone", "wall-wood", "wall2-wood", "waraxe",
            "warhammer", "web", "wood", "tunic", "pantaloons", "boots",
            // the ember forest's grove trees and meadow grass, planted by id (Lab tiles are also items)
            "grove-oak-a", "grove-oak-b", "grove-spruce-a", "grove-spruce-b", "grove-round-a", "grove-round-b",
            "grove-low-a", "grove-low-b",
            "meadow-grass-a", "meadow-grass-b", "meadow-grass-c", "meadow-grass-d", "meadow-grass-e", "meadow-grass-f",
        ],
        races: [
            "bee", "chicken", "deer", "horse", "human", "orc", "rabbit", "redspider", "skeleton", "slime",
            "snake", "spikes1", "wasp", "wolfman", "zombie",
        ],
        /** Encounter tables engine code reads by name, with the races it reads from each. */
        encounters: { emberWildlife: ["rabbit", "chicken"] },
    },
} as const
